import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import EmptyState from '../../components/EmptyState';
import StatusBadge from '../../components/StatusBadge';
import { usePlatformPermissions } from '../../hooks/usePlatformPermissions';
import accountApi from '../../services/accountApi';

function unwrapList(data) {
  if (Array.isArray(data)) return { results: data, count: data.length };
  return data || { results: [], count: 0 };
}

function PlatformTenantsPage() {
  const { hasPlatformPermission } = usePlatformPermissions();
  const canSuspend = hasPlatformPermission('platform.tenant.suspend');
  const [partners, setPartners] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');

  const query = useMemo(() => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (typeFilter) params.partner_type = typeFilter;
    if (search.trim()) params.q = search.trim();
    return params;
  }, [statusFilter, typeFilter, search]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = unwrapList(await accountApi.listPlatformPartners(query));
      setPartners(data.results || []);
      setCount(data.count ?? (data.results || []).length);
    } catch (err) {
      setError(err.message || 'Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, typeFilter]);

  const toggleStatus = async (partner) => {
    if (!canSuspend) return;
    try {
      const action = partner.status === 'active' ? 'suspend' : 'reactivate';
      await accountApi.suspendPlatformPartner(partner.id, { action });
      await load();
    } catch (err) {
      setError(err.message || 'Failed to update partner status');
    }
  };

  return (
    <div>
      <h1 className="product-page-title">Partners</h1>
      <p className="product-page-subtitle">All partner organizations on the platform.</p>
      <div className="mb-3">
        {hasPlatformPermission('platform.tenant.manage') && (
          <Link to="/platform/partners/new" className="btn btn-primary btn-sm">
            Create Partner
          </Link>
        )}
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="product-card mb-3">
        <Form
          className="row g-2 align-items-end"
          onSubmit={(e) => {
            e.preventDefault();
            load();
          }}
        >
          <div className="col-md-4">
            <Form.Label>Search</Form.Label>
            <Form.Control
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name or slug"
            />
          </div>
          <div className="col-md-3">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="suspended">Suspended</option>
              <option value="archived">Archived</option>
            </Form.Select>
          </div>
          <div className="col-md-3">
            <Form.Label>Type</Form.Label>
            <Form.Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">All</option>
              <option value="studio">Studio</option>
              <option value="agency">Agency</option>
              <option value="enterprise">Enterprise</option>
              <option value="sandbox">Sandbox</option>
              <option value="internal">Internal</option>
              <option value="fixture">Fixture</option>
              <option value="customer">Legacy customer</option>
            </Form.Select>
          </div>
          <div className="col-md-2">
            <Button type="submit" variant="outline-primary" className="w-100">
              Apply
            </Button>
          </div>
        </Form>
      </div>

      {loading && (
        <div className="loading-state">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading…
        </div>
      )}

      {!loading && (
        <div className="product-card">
          <div className="small text-muted mb-2">{count} partner{count === 1 ? '' : 's'}</div>
          <Table responsive className="product-table" size="sm">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Type</th>
                <th>Bundle</th>
                <th>Members</th>
                <th>Projects</th>
                <th>Jobs</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {partners.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <EmptyState
                      title="No partners found"
                      description="Try clearing filters or create a Partner."
                      action={
                        hasPlatformPermission('platform.tenant.manage') ? (
                          <Link to="/platform/partners/new" className="btn btn-sm btn-primary">
                            Create Partner
                          </Link>
                        ) : null
                      }
                    />
                  </td>
                </tr>
              ) : (
                partners.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <Link to={`/platform/partners/${p.id}`}>{p.name}</Link>
                      <div className="mono small text-muted">{p.slug}</div>
                    </td>
                    <td>
                      <StatusBadge status={p.status} />
                    </td>
                    <td>{p.partner_type || p.tenant_type}</td>
                    <td className="small">{p.capability_bundle || '—'}</td>
                    <td>{p.counts?.members ?? '—'}</td>
                    <td>{p.counts?.projects ?? '—'}</td>
                    <td>{p.counts?.jobs ?? '—'}</td>
                    <td className="text-end">
                      {canSuspend && (
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => toggleStatus(p)}
                        >
                          {p.status === 'active' ? 'Suspend' : 'Reactivate'}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default PlatformTenantsPage;
